'use client'

import { useCallback } from 'react'
import type { GetGaTagInfoResponse } from 'tokyu-hooks/lib/response/smartclub/analytics'

import { fetchGaTagInfo } from './fetchGaTagInfo'

interface CacheEntry {
  data: GetGaTagInfoResponse
  timestamp: number
  smartClubId: number
}

const CACHE_DURATION = 10 * 60 * 1000 // 10 minutes
const CACHE_KEY = 'ga-tag-info-cache'

/**
 * NOTE:
 * for this hook we are not using tanstack query
 * because we want to cache the data in the localStorage
 * and its only called once in root layout
 */
interface GaTagInfoState {
  fetchData: () => Promise<GetGaTagInfoResponse | null>
}

const useQueryGaTagInfo = ({
  smartClubId,
  smartClubAccessToken,
}: {
  smartClubId?: number
  smartClubAccessToken?: string
}): GaTagInfoState => {
  const fetchData =
    useCallback(async (): Promise<GetGaTagInfoResponse | null> => {
      if (!smartClubId || !smartClubAccessToken) return null

      // Try to get cached data first
      if (typeof window !== 'undefined') {
        try {
          const cached = localStorage.getItem(CACHE_KEY)
          if (cached) {
            const cacheEntry: CacheEntry = JSON.parse(cached)
            const now = Date.now()

            // Check if cache is valid (not expired and for same user)
            if (
              cacheEntry.smartClubId === smartClubId &&
              now - cacheEntry.timestamp < CACHE_DURATION
            ) {
              return cacheEntry.data
            }

            // Cache expired or for different user, remove it
            localStorage.removeItem(CACHE_KEY)
          }
        } catch {
          localStorage.removeItem(CACHE_KEY)
        }
      }

      // No valid cache, fetch fresh data
      try {
        const fetchFn = fetchGaTagInfo(smartClubId, smartClubAccessToken)
        const result = await fetchFn()

        // Cache the fresh data
        if (typeof window !== 'undefined' && result) {
          try {
            const cacheEntry: CacheEntry = {
              data: result,
              timestamp: Date.now(),
              smartClubId,
            }
            localStorage.setItem(CACHE_KEY, JSON.stringify(cacheEntry))
          } catch {
            // Ignore localStorage errors
          }
        }

        return result
      } catch (err) {
        throw err instanceof Error ? err : new Error('Unknown error')
      }
    }, [smartClubId, smartClubAccessToken])

  return {
    fetchData,
  }
}

export default useQueryGaTagInfo
